import Navbar from 'src/components/HorizontalLayout/Navbar';
import React, { useEffect, useState, useMemo, ChangeEvent, useCallback, useRef, memo } from "react"
import { useNavigate } from "react-router-dom";
import ToggleModal from "src/pages/Users/ClientUsers/ToggleModal";
import debounce from 'lodash.debounce';  // Import debounce
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  Input,
  Button,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  UncontrolledAccordion
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useSelector, useDispatch } from "react-redux";
import { getClients } from 'src/slices/Clients/thunk';
import { useAgency } from 'src/components/Common/agencyBrandingData';
import { toast } from 'react-toastify';
import axios from "axios";
import axiosInstance from 'src/common/axiosInstance';
import ClientCardLoader from '../../components/Common/ClientCardLoader';
import { usePageAccess, PAGE_ACCESS_TYPES } from '../../components/Common/useAuthRedirect';
import useIsMobile from 'src/hooks/useIsMobile';
import Pagination from 'src/components/Common/Pagination';
import MemberIcon from 'src/assets/images/Member.svg';
import OrderIcon from 'src/assets/images/Order.svg';
import ClientMarginModal from "./ClientMarginModal";
import "./clients.css"

interface Client {
  client_name: string;
  client_id: number;
  account: any;
  agency_name: string;
  member_count: number;
  order_count: number;
  is_active?:  boolean;
}
interface Account {
  account_id: string;
  account_name: string;
  users_id?: number;
}
const Clients = () => {
  const dispatch = useDispatch();
  const agency = useAgency();
  const navigate = useNavigate();
  const [modalLarge, setModalLarge] = useState(false);
  const { clients } = useSelector((state: any) => state.clients);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(false);
  const { user } = useSelector((state: any) => state.login);
  const [clientName, setClientName] = useState('');
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [agencyId, setAgencyId] = useState<string>("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [clientList, setClientList] = useState<Client | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage, setClientsPerPage] = useState(20);
  const isMobile = useIsMobile();
  const [searchValue, setSearchValue] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedAccountName, setSelectedAccountName] = useState('');
  const [disabledCurrentPage, setDisabledCurrentPage] = useState(1);
  const [disabledClientsPerPage, setDisabledClientsPerPage] = useState(20);
  const [marginModalOpen, setMarginModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);


  usePageAccess(PAGE_ACCESS_TYPES.ADMIN_SPECIFIC_ONLY);

  function togLarge() {
    setClientName('');
    setModalLarge(!modalLarge);
    removeBodyCss();
    setSelectedAccount('');
    setSelectedAccountName('');
    setSearchValue('');
    setAccounts([]);
    setIsOpen(false);
  }

  function removeBodyCss() {
    document.body.classList.add("no_padding");
  }

  useEffect(() => {
    if ('agency_id' in (agency?.data || {})) {
      setAgencyId(agency.data.agency_id ?? agency.data.Id);
    }

    dispatch(getClients(
      agency.data.agency_id ?? agency.data.Id,
      navigate,
      1,
      999,
      searchTerm,
      false
    ))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [dispatch, agency.data.agency_id, navigate, searchTerm]);
  // Debounce search input to reduce API calls
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  }, [debouncedSearch]);


  const fetchAccounts = useMemo(
    () => debounce(async (query: string) => {
      if (query.length < 3) {
        setAccounts([]);
        setIsOpen(false)
        return;
      }
      try {
        const response = await axiosInstance.get(`/accounts?search`, {
          params: {
            acc: query,
          },
        });
        const accountsData = response.data?.accounts || response.data || [];
        setAccounts(Array.isArray(accountsData) ? accountsData : []);
        setIsOpen(Array.isArray(accountsData) && accountsData.length > 0)
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setAccounts([]);
        setIsOpen(false);
      }
    }, 500),
    []);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      fetchAccounts.cancel();
    };
  }, [fetchAccounts]);

  const searchAccount = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target;
    setSearchValue(value);
    fetchAccounts(value);
  };
  const handleAccountSelect = (account: any) => {
    setSelectedAccount(account.account_id);
    setSelectedAccountName(account.account_name);
    setAccounts([]);
    setSearchValue('');
  };

  const totalPages = clients?.total_pages || 1; // Use server total pages
  const totalCount = clients?.total || 0; // Use server total count
  const emojiRegex = /[\p{Extended_Pictographic}]/u;

  const handleOnClick = async () => {
    setProgress(true);
    if (clientName.trim() === '') {
      toast.error("Please enter client name!");
      setProgress(false);
      return;
    }

    if (["admin", "AE", "AO"].includes(user?.user?.user_type) && selectedAccount === '') {
      toast.error("Please select an account to add client!");
      setProgress(false);
      return;
    }

    if (clientName.length > 100) {
      toast.error("Client name should not exceed 100 characters!");
      setProgress(false);
      return;
    }

    if (emojiRegex.test(clientName)) {
      toast.error("Invalid client name. Emojis are not permitted.");
      setProgress(false);
      return;
    }

    try {
      const response = await axiosInstance.post(`/add-agency-client`, {
        agency_id: selectedAccount ? selectedAccount : agencyId,
        client_name: clientName.trim(),
        users_id: user.user.users_id
      });

      if (response.data.status === 201) {
        toast.success(response.data.message);
        setModalLarge(false);
        setProgress(false);       
        setCurrentPage(1);
        setDisabledCurrentPage(1);
        dispatch(getClients(
          agency.data.agency_id ?? agency.data.Id,
          navigate,
          1,
          9999,
          searchTerm,
          false
        ));
      } else {
        setProgress(false);
        toast.error(response.data.message)
      }
    } catch (error) {
      setProgress(false);
      if (axios.isAxiosError(error)) {
        if (error.response && error.response.status === 409) {
          toast.error(error.response.data.message)
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } else {
        console.error("Error:", error);
      }
    }
  };

  const handleToggleStatus = async (client?: Client) => {
    const targetClient = client || clientList;
    if (!targetClient) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post(`/toggle-client/${targetClient.client_id}`);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        
        setCurrentPage(1);
        setDisabledCurrentPage(1);
        
        dispatch(getClients(
          agency.data.agency_id ?? agency.data.Id,
          navigate,
          1,
          9999,
          searchTerm,
          false
        ));
        setDeleteModal(false);
      }
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };


  const allClients = clients?.data || [];
  const enabledClients = allClients.filter((c: Client) => c.is_active !== false);
  const disabledClients = allClients.filter((c: Client) => c.is_active === false);
  const enabledStartIndex = (currentPage - 1) * clientsPerPage;
  const enabledEndIndex = enabledStartIndex + clientsPerPage;
  const paginatedEnabledClients = enabledClients.slice(enabledStartIndex, enabledEndIndex);
  const enabledTotalPages = Math.ceil(enabledClients.length / clientsPerPage);
  const disabledStartIndex = (disabledCurrentPage - 1) * disabledClientsPerPage;
  const disabledEndIndex = disabledStartIndex + disabledClientsPerPage;
  const paginatedDisabledClients = disabledClients.slice(disabledStartIndex, disabledEndIndex);
  const disabledTotalPages = Math.ceil(disabledClients.length / disabledClientsPerPage);

  const DelayedMessage = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
      <div className="text-center">
        <i className="mdi mdi-account-alert-outline" style={{ fontSize: "48px", color: "#6c757d" }}></i>
        <p style={{ fontSize: "18px", color: "#6c757d", marginTop: "10px" }}>No clients found</p>
      </div>
    </div>
  );

  // Memoized delete handler
  const handleDeleteClick = useCallback((client: Client) => {
    setClientList(client);
    setDeleteModal(true);
  }, []);

  // Check if user can delete and show account
  const showDeleteButton = useMemo(() => !agency.data.Id, [agency.data.Id]);
  const isAgencyAdmin = useMemo(() => !!agency.data.Id, [agency.data.Id]);
  const isQ1User = useMemo(() => ["admin", "AE", "AO"].includes(user?.user?.user_type), [user?.user?.user_type]);
  const showAccount = isQ1User;
  const showCogIcon = isAgencyAdmin || isQ1User;

  const handleCogClick = useCallback((client: Client) => {
    setSelectedClient(client);
    setMarginModalOpen(true);
  }, []);

  // Memoized ClientCard component to prevent unnecessary re-renders
  const ClientCard = memo(({ client }: { client: Client }) => {
    return (
      <Col xs={12} sm={6} md={4} lg={3} className="mb-3">
        <Card 
          className="h-100" 
          style={{ 
            marginBottom: '0',
            opacity: client.is_active === false ? 0.8 :  1,
          }}
        >
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              {/* Left side: name + members/orders */}
              <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center gap-1 mb-2">
                  <h5
                    className="card-title mb-0 text-truncate"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={client.client_name}
                  >
                    <strong>{client.client_name}</strong>
                  </h5>
                  {showCogIcon && client.is_active !== false && (
                    <button
                      className="btn-margin-cog"
                      onClick={() => handleCogClick(client)}
                      title="Configure Margins"
                    >
                      <i className="mdi mdi-cog font-size-16"></i>
                    </button>
                  )}
                </div>
                {showAccount && client.account?.Name && (
                  <div style={{ minHeight: '24px' }} className="mb-2">
                    <p style={{ maxWidth: '250px', overflow: 'hidden', whiteSpace: 'nowrap' }} title={client.account?.Name}>{client.account?.Name}</p>
                  </div>
                )}
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center">
                    <img src={MemberIcon} alt="Members" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
                    <span className="text-muted">Members: {client.member_count}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <img src={OrderIcon} alt="Orders" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
                    <span className="text-muted">Orders: {client.order_count}</span>
                  </div>
                </div>
              </div>

              {/* Right side: toggle */}
              <div className="d-flex align-items-center gap-2">
                {showDeleteButton && (
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={client.is_active !== false}
                      onChange={() => {
                        if (client.is_active === false) {
                          handleToggleStatus(client);
                        } else {
                          handleDeleteClick(client);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

          </CardBody>
        </Card>
      </Col>
    );
  });
  ClientCard.displayName = 'ClientCard';



  const [isOpen, setIsOpen] = useState(false); // control dropdown visibility
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <React.Fragment>
      <style>
        {`
          .accordion-button {
            background-color: transparent !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <ToggleModal
        show={deleteModal}
        onToggleClick={() => handleToggleStatus()}
        onCloseClick={() => setDeleteModal(false)}
        isEnabling={clientList?.is_active === false}
        clientName={clientList?.client_name}
      />
      <div className="page-content" style={{ marginTop: "0" }}>
        <Navbar />
        <Container fluid style={{ maxWidth: "100%", marginTop: 10 }}>
          <Breadcrumbs title="Settings" breadcrumbItem="Clients" titleLink='/branding' showLeftTitle={false}/>
          <Row>
            <Col lg={12}>
              <Card style={{ marginBottom: "20px" }}>
                <CardBody style={{
                  padding: '8px 5px'
                }}>
                  <Row className="justify-content-end align-items-center gx-3 gy-3">
                    <Col xs={12} sm="auto">
                      <Input
                        type="text"
                        placeholder="Search"
                        onChange={handleSearchChange}
                        className="w-100"
                      />
                    </Col>
                    <Col xs={12} sm="auto">
                      <button
                        type="button"
                        className="btn btn-primary w-100 button-primary"
                        onClick={togLarge}
                      >
                        <i className="uil uil-plus me-1"></i>Add Client
                      </button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <UncontrolledAccordion defaultOpen={['1', '2']} stayOpen toggle={() => {}}>
                <Card className="mb-3" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                  <AccordionItem style={{ border: "none", boxShadow: "none", background: "transparent", padding: 0 }}>
                    <AccordionHeader targetId="1" className="custom-border-bottom">
                      <h5 className="mb-0" style={{ fontSize: "16px" }}>
                        Clients <span className="text-muted">({loading ? 0 : enabledClients.length})</span>
                      </h5>
                    </AccordionHeader>
                    <AccordionBody accordionId="1">
                      {loading ? (
                        <ClientCardLoader />
                      ) : (
                        <>
                          <Row>
                            {paginatedEnabledClients.length > 0 ? (
                              paginatedEnabledClients.map((client: Client) => (
                                <ClientCard key={client.client_id} client={client} />
                              ))
                            ) : (
                              <DelayedMessage />
                            )}
                          </Row>
                          {enabledClients.length > 0 && (
                            <Pagination
                              totalPages={enabledTotalPages}
                              currentPage={currentPage}
                              currentCount={paginatedEnabledClients.length}
                              onPageChange={setCurrentPage}
                              pageSize={clientsPerPage}
                              onPageSizeChange={(size) => {
                                setClientsPerPage(size);
                                setCurrentPage(1);
                              }}
                              menuPlacement='top'
                            />
                          )}
                        </>
                      )}
                    </AccordionBody>
                  </AccordionItem>
                </Card>

                <Card className="mb-3" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                  <AccordionItem style={{ border: "none", boxShadow: "none", background:  "transparent", padding: 0 }}>
                    <AccordionHeader targetId="2">
                      <h5 className="mb-0" style={{ fontSize: "16px" }}>
                        Disabled Clients <span className="text-muted">({loading ? 0 : disabledClients.length})</span>
                      </h5>
                    </AccordionHeader>
                    <AccordionBody accordionId="2">
                      {loading ? (
                        <ClientCardLoader />
                      ) : (
                        <>
                          <Row>
                            {paginatedDisabledClients.length > 0 ? (
                              paginatedDisabledClients.map((client: Client) => (
                                <ClientCard key={client.client_id} client={client} />
                              ))
                            ) : (
                              <DelayedMessage />
                            )}
                          </Row>
                          {disabledClients.length > 0 && (
                            <Pagination
                              totalPages={disabledTotalPages}
                              currentPage={disabledCurrentPage}
                              currentCount={paginatedDisabledClients.length}
                              onPageChange={setDisabledCurrentPage}
                              pageSize={disabledClientsPerPage}
                              onPageSizeChange={(size) => {
                                setDisabledClientsPerPage(size);
                                setDisabledCurrentPage(1);
                              }}
                              menuPlacement='top'
                            />
                          )}
                        </>
                      )}
                    </AccordionBody>
                  </AccordionItem>
                </Card>
              </UncontrolledAccordion>
            </Col>
          </Row>

          {/* Add Client Modal */}
          <Modal
            size="lg"
            isOpen={modalLarge}
            toggle={togLarge}
            modalClassName={"general-custom-modal modal-top-mobile"}
          >
            <div className="modal-header">
              <h5 className="modal-title mt-0" id="myLargeModalLabel">
                Add New Client
              </h5>
              <button
                onClick={togLarge}
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true"> </span>
              </button>
            </div>
            <div className="modal-body">
              <Row>
                <Col xl={12}>
                  <div>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="fw-bold mb-2" htmlFor="gen-info-name-input">Client Name</label>
                        <input
                          type="text"
                          className="form-control"
                          id="gen-info-name-input client_name"
                          placeholder="Add Client Name"
                          value={clientName}
                          onChange={(e) => setClientName(e.target.value)}
                        />
                      </Col>
                    </Row>
                    {["admin", "AE", "AO"].includes(user.user.user_type) && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <div className="col position-relative">
                            <label className="fw-bold mb-2">Search Account</label>

                            {/* Modern search box with icon */}
                            <div className="input-group shadow-sm rounded overflow-hidden">
                              <Input
                                type="text"
                                name="searchAccount"
                                value={searchValue}
                                onChange={searchAccount}
                                onBlur={() => {
                                  setTimeout(() => {
                                    setSearchValue('');
                                    setIsOpen(false);
                                  }, 200);
                                }}
                                className="form-control"
                                placeholder="Type to search..."
                              />
                            </div>

                            {/* Results list */}
                            {(isOpen && accounts.length > 0) && (
                              <div
                                ref={dropdownRef}
                                className="position-absolute w-100 bg-white border rounded shadow mt-2 custom-scrollbar"
                                style={{ maxHeight: "220px", overflowY: "auto", zIndex: 1000 }}
                              >
                                {accounts.map((account) => (
                                  <div
                                    key={account.account_id}
                                    className="p-2 px-3 hover-bg-light d-flex align-items-center justify-content-between"
                                    style={{ cursor: "pointer" }}
                                    onMouseDown={() => handleAccountSelect(account)}
                                  >
                                    <span>{account.account_name}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Selected account shown as a modern badge with remove option */}
                            {selectedAccountName && (
                              <div className="mt-3">
                                <span className=" fs-6 border px-3 py-2 rounded-pill shadow-sm d-inline-flex align-items-center">
                                  {selectedAccountName}
                                  <i
                                    className="mdi mdi-close-circle ms-2"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => { setSelectedAccountName(""); setSelectedAccount("") }}
                                  ></i>
                                </span>
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col md={12} style={{ textAlign: 'right' }}>
                        {/* <div className="d-flex justify-content-center justify-content-sm-end"> */}
                        <Button
                          color="primary"
                          type="submit"
                          className="btn btn-primary button-primary"
                          onClick={handleOnClick}
                          disabled={progress}
                          style={{ width: isMobile ? '100%' : 'auto' }}
                        >
                          {progress ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            'Add'
                          )}
                        </Button>
                      </Col>
                    </Row>
                    {/* </div> */}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>

          <ClientMarginModal
            isOpen={marginModalOpen}
            toggle={() => setMarginModalOpen(false)}
            client={selectedClient}
            readOnly={isQ1User}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Clients;
